import { DOCUMENT, DecimalPipe, LowerCasePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, Input, OnInit, ViewEncapsulation, booleanAttribute, inject, numberAttribute, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { APP_CONSTANTS, CURRENCY, CUSTOM_PLAN_CONFERENCE_TYPE, CUSTOM_PLAN_GROUP_APPOINTMENT_TYPE, CUSTOM_PLAN_STORE_TYPE, CUSTOM_PLAN_USER_TYPE, DECIMAL_DIGITS, DECIMAL_LOCALE, INTERVAL, LANGUAGES, PRICING_PLAN_PRECEDENCE, RENDER_MODE, SALES_INTERVAL, SUBSCRIPTION, SUBSCRIPTION_IDS, SUPPORTED_LANGUAGES } from '@constants/app.constants';
import { DASHBOARD_PRICING_JSON } from '@constants/dashboard.constants';
import { EXTRA_CONFERENCE_COST, EXTRA_GROUP_APPOINTMENT_COST, EXTRA_STORE_COST, EXTRA_USER_COST, MAX_LIMIT, MIN_CONFERENCE_LIMIT, MIN_LIMIT, SALES_TOKEN } from '@constants/pricing.constants';
import { SALES_PRICING_JSON } from '@constants/sales.constants';
import { WEBSITE_PRICING_JSON } from '@constants/website.constants';
import { Currency, IntervalType, PricingDetails, RenderModeType, SalesIntervalType, SubscriptionType, SupportedLanguages } from '@db-models/pricing-db.model';
import { SubscriptionModel } from '@db-models/subscribe-db.model';
import { MaxInputValueDirective } from '@directives/max-input-value.directive';
import { environment } from '@env/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalioSafePipe } from '@pipes/calio-safe.pipe';
import { HelperService } from '@services/util-service/helper.service';
import { LoggerService } from '@services/util-service/logger.service';

const directives = [MaxInputValueDirective];
const modules = [TranslateModule, NgClass, NgTemplateOutlet, FormsModule];
const pipes = [LowerCasePipe, DecimalPipe, CalioSafePipe];

@Component({
  selector: 'calenso-pricing-table',
  templateUrl: './calenso-pricing-table.component.html',
  styleUrl: './calenso-pricing-table.component.scss',
  standalone: true,
  imports: [...modules, ...pipes, ...directives],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class CalensoPricingTableComponent implements OnInit {

  @Input('language') set lang(language: SupportedLanguages) {
    this.setLanguage(language);
  };

  @Input('render-mode') set RenderMode(renderMode: RenderModeType) {
    this._renderModeSignal.set(Object.values(RENDER_MODE).includes(renderMode) ? renderMode : RENDER_MODE.WEBSITE);
    this.setPricingJson();
  };

  @Input('currency') set Currency(currency: Currency) {
    this.setCurrency(currency);
  };

  @Input('current-subscription') set setCurrentSubscription(currentSubscription: 'undefined' | 'null') {
    if (this._renderModeSignal() === RENDER_MODE.DASHBOARD && currentSubscription && (currentSubscription !== "undefined") && (currentSubscription !== "null")) {
      this._currentSubscriptionSignal.set(JSON.parse(currentSubscription) as SubscriptionModel);
      // show currenctly active plan with interval
      this._intervalSignal.set(this._currentSubscriptionSignal()?.subscription_interval);
      this.currentPlanPrecedenceSignal.set(this.pricingPlanPrecedence[this._currentSubscriptionSignal()?.subscription_type_id]);
      this.setCurrentSubscriptionChanges();
      this.isPartnerInTrialModeSignal.set(!!this._currentSubscriptionSignal()?.is_trial);
    }
  };

  @Input('subscription') set setSubscription(preSelectedSubscription: SubscriptionType) {
    if (this._renderModeSignal() === RENDER_MODE.DASHBOARD && Object.values(SUBSCRIPTION).includes(preSelectedSubscription)) {
      this._preSelectedSubscriptionSignal.set(preSelectedSubscription);
      this._preSelectedIntervalSignal() && this.setPreDefineSelectedPlan();
    }
  };

  @Input('interval') set setIntervals(preSelectedInterval: IntervalType) {
    if (this._renderModeSignal() === RENDER_MODE.DASHBOARD && Object.values(INTERVAL).includes(preSelectedInterval)) {
      this._preSelectedIntervalSignal.set(preSelectedInterval);
      this._preSelectedSubscriptionSignal() && this.setPreDefineSelectedPlan();
    }
  };

  @Input('popular') set setPopularPlan(popularPlan: SubscriptionType) {
    this.setPopular(popularPlan);
  };

  @Input('show-custom-plan-section') set setCustomPlanSection(showCustomPlan: string) {
    (showCustomPlan === 'true') && (this._showCustomPlanSectionSignal.set(true));
  };

  @Input('token') salesToken: string;
  @Input({ alias: 'show-more-interval', transform: booleanAttribute }) set showMoreInterval(moreInterval: boolean) {
    if (moreInterval === true && this._renderModeSignal() === this.sales) {
      this._showMoreIntervalSignal.set(true);
      this.checkForSalesMode();
    }
  };

  @Input({ alias: 'show-enterpriselite-plan', transform: booleanAttribute }) set showEnterpriseLitePlan(_showEnterpriseLitePlan: boolean) {
    if (_showEnterpriseLitePlan === true && this._renderModeSignal() === this.sales) {
      this._showEnterpriseLitePlanSignal.set(true);
      this.checkForSalesMode();
    }
  };

  @Input({ alias: 'show-user-based-pricing', transform: booleanAttribute }) set showUserBasedPricing(_showUserBasedPricing: boolean) {
    (_showUserBasedPricing === true && this._renderModeSignal() === this.website) && (this._showUserBasedPricingSignal.set(true));
    this.setPricingJson();
  };

  @Input({ alias: 'minimum-subscription', transform: numberAttribute }) set minimumSubscription(_minimumSubscription: number) {
    Object.keys(PRICING_PLAN_PRECEDENCE).map(Number).includes(_minimumSubscription) && (this._minimumSubscriptionSignal.set(_minimumSubscription));
    this.setPricingJson();
  };

  @Input({ alias: 'hide-monthly-interval', transform: booleanAttribute }) set hideMonthlyInterval(_hideMonthlyInterval: boolean) {
    (_hideMonthlyInterval === true) && (this._hideMonthlyIntervalSignal.set(true), this.setInterval(this.yearly));
  };

  private helperService = inject(HelperService);
  private document = inject(DOCUMENT);
  private translate = inject(TranslateService);

  private readonly dashboardPricingList = DASHBOARD_PRICING_JSON;
  private readonly websitePricingList = WEBSITE_PRICING_JSON;
  private readonly salesPricingList = SALES_PRICING_JSON;
  protected readonly deployUrl = environment.deployUrl
  protected readonly monthly = INTERVAL.MONTHLY;
  protected readonly yearly = INTERVAL.YEARLY;
  protected readonly year_2 = SALES_INTERVAL.YEAR_2;
  protected readonly year_3 = SALES_INTERVAL.YEAR_3;
  protected readonly website = RENDER_MODE.WEBSITE;
  protected readonly dashboard = RENDER_MODE.DASHBOARD;
  protected readonly sales = RENDER_MODE.SALES;
  protected readonly pricingPlanPrecedence = PRICING_PLAN_PRECEDENCE;
  protected readonly enterpriseId = SUBSCRIPTION_IDS.enterprise;
  protected readonly eur = CURRENCY.EUR;
  protected readonly chf = CURRENCY.CHF;
  protected readonly usd = CURRENCY.USD;
  protected readonly gbp = CURRENCY.GBP;
  protected readonly en_US = SUPPORTED_LANGUAGES.EN_US;
  protected readonly de_CH = SUPPORTED_LANGUAGES.DE_CH;
  protected readonly maxLimit = MAX_LIMIT;
  protected readonly minLimit = MIN_LIMIT;
  protected readonly minConferenceLimit = MIN_CONFERENCE_LIMIT;
  protected readonly extraUserCost = EXTRA_USER_COST;
  protected readonly extraGroupAppointmentCost = EXTRA_GROUP_APPOINTMENT_COST;
  protected readonly extraStoreCost = EXTRA_STORE_COST;
  protected readonly extraConferenceCost = EXTRA_CONFERENCE_COST;
  protected readonly customPlanUserType = CUSTOM_PLAN_USER_TYPE;
  protected readonly customPlanStoreType = CUSTOM_PLAN_STORE_TYPE;
  protected readonly customPlanGroupAppointmentType = CUSTOM_PLAN_GROUP_APPOINTMENT_TYPE;
  protected readonly customPlanConferenceType = CUSTOM_PLAN_CONFERENCE_TYPE;
  protected readonly decimalDigits = DECIMAL_DIGITS;
  protected readonly decimalLocal = DECIMAL_LOCALE;
  protected readonly subscriptionIds = SUBSCRIPTION_IDS;
  protected readonly currentYear = new Date().getFullYear();
  protected readonly brandParams = {
    brandName: environment.brandName,
    websiteUrl: environment.websiteUrl,
  };

  protected pricingListSignal = signal(WEBSITE_PRICING_JSON);
  protected currentPlanPrecedenceSignal = signal(0);
  protected isCustomPlanSelectionEnabledSignal = signal(false);
  protected customPlanUserSignal = signal(this.minLimit);
  protected customPlanGroupAppointmentSignal = signal(this.minLimit);
  protected customPlanStoreSignal = signal(this.minLimit);
  protected customPlanConferenceSignal = signal(this.minConferenceLimit);
  protected customSelectedPlanSignal = signal<PricingDetails | undefined>(undefined);
  protected appliedPlanSignal = signal<PricingDetails | undefined>(undefined);
  protected customPlanTotalAmountSignal = signal(0);
  protected currencySymbolSignal = signal('');
  protected isPartnerInTrialModeSignal = signal(false);
  protected availablePlansSignal = signal([]);

  protected _languageSignal = signal<SupportedLanguages>(undefined);
  protected _currencySignal = signal<Currency>(undefined);
  protected _renderModeSignal = signal<RenderModeType>(RENDER_MODE.WEBSITE);
  protected _preSelectedSubscriptionSignal = signal<SubscriptionType>(undefined);
  protected _currentSubscriptionSignal = signal<SubscriptionModel>(undefined);
  protected _intervalSignal = signal<IntervalType | SalesIntervalType>(INTERVAL.YEARLY);
  protected _preSelectedIntervalSignal = signal<IntervalType>(undefined);
  protected _popularPlanSignal = signal<SubscriptionType>(undefined);
  protected _showCustomPlanSectionSignal = signal<boolean>(false);
  protected _showMoreIntervalSignal = signal<boolean>(false);
  protected _showEnterpriseLitePlanSignal = signal<boolean>(false);
  protected _showUserBasedPricingSignal = signal<boolean>(false);
  protected _minimumSubscriptionSignal = signal<number>(undefined);
  protected _hideMonthlyIntervalSignal = signal<boolean>(false);

  constructor() {
    this.pricingListSignal.set(this.helperService.setupMaxLimiteInPricingList(this.pricingListSignal()));
  }

  ngOnInit(): void {
    this.checkForSalesMode();

    LoggerService.log("[Pricing Page Inputs]", {
      "language": this._languageSignal(),
      "currency": this._currencySignal(),
      "renderMode": this._renderModeSignal(),
      "preSelectedSubscription": this._preSelectedSubscriptionSignal(),
      "currentSubscription": this._currentSubscriptionSignal(),
      "interval": this._intervalSignal(),
      "pricingList": this.pricingListSignal(),
      "token": this.salesToken,
      "_showEnterpriseLitePlan": this._showEnterpriseLitePlanSignal(),
      "_showUserBasedPricing": this._showUserBasedPricingSignal(),
    });

    this.validateInputs();
    this.pricingTableProjectLoaded();

    // Default custom plan is enabled then calculate price
    this.calculateCustomPlanPrice();
  }

  protected setLanguage(language?: SupportedLanguages): void {
    if ([SUPPORTED_LANGUAGES.DE_CH, SUPPORTED_LANGUAGES.EN_US].includes(language)) {
      this.translate.setDefaultLang(language);
    } else {
      const browserLang = this.translate.getBrowserCultureLang();
      if (browserLang && LANGUAGES.find(language => language.locale === browserLang.replace('-', '_'))) {
        this.translate.setDefaultLang(browserLang.replace('-', '_'));
      } else {
        this.translate.setDefaultLang(APP_CONSTANTS.DEFAULT_LANG);
      }
    }
    this._languageSignal.set(this.translate.getDefaultLang() as (SupportedLanguages));
  }

  protected setCurrency(currency?: Currency): void {
    if ([CURRENCY.CHF, CURRENCY.EUR, CURRENCY.USD, CURRENCY.GBP].includes(currency)) {
      this._currencySignal.set(currency);
      this.currencySymbolSignal.set(currency);
    } else {
      this._currencySignal.set(CURRENCY.EUR);
      this.currencySymbolSignal.set(CURRENCY.EUR);
    }
  }

  private setInterval(interval: IntervalType = INTERVAL.YEARLY): void {
    if ([INTERVAL.YEARLY, INTERVAL.MONTHLY].includes(interval)) {
      this._intervalSignal.set(interval);
    }
  }

  private setCurrentSubscriptionChanges(): void {
    this.setInterval(this._currentSubscriptionSignal()?.subscription_interval);
    (this._currentSubscriptionSignal()?.additional_users) && (this.changeUserProfile(this._currentSubscriptionSignal()?.additional_users, this.customPlanUserType));
    (this._currentSubscriptionSignal()?.additional_stores) && (this.changeUserProfile(this._currentSubscriptionSignal()?.additional_stores, this.customPlanStoreType));
    (this._currentSubscriptionSignal()?.additional_events) && (this.changeUserProfile(this._currentSubscriptionSignal()?.additional_events, this.customPlanGroupAppointmentType));
    (this._currentSubscriptionSignal()?.additional_conferences) && (this.changeUserProfile(this._currentSubscriptionSignal()?.additional_conferences, this.customPlanConferenceType));
  }

  private setPreDefineSelectedPlan(): void {
    if (this._preSelectedSubscriptionSignal() === SUBSCRIPTION.ENTERPRISE) {
      LoggerService.warn("Not able to upgrade to ENTERPRISE plan form UI, Need to contact support.");
      return;
    }

    if (this.currentPlanPrecedenceSignal() === this.pricingPlanPrecedence?.[this.subscriptionIds?.[this._preSelectedSubscriptionSignal()]]) {
      if (this._currentSubscriptionSignal()?.subscription_interval === this.monthly && this._preSelectedIntervalSignal() === this.monthly) {
        LoggerService.warn("Current selected plan is already activated");
      } else if (this._currentSubscriptionSignal()?.subscription_interval === this.monthly && this._preSelectedIntervalSignal() === this.yearly) {
        LoggerService.log("Current plan is interval upgrade");
        const plan = this.pricingListSignal().find(planDetails => planDetails.name.toUpperCase() === this._preSelectedSubscriptionSignal()?.toUpperCase());
        this.submit(plan, this._preSelectedIntervalSignal(), this._currencySignal());
      }
    } else if (this.currentPlanPrecedenceSignal() < this.pricingPlanPrecedence?.[this.subscriptionIds?.[this._preSelectedSubscriptionSignal()]]) {
      LoggerService.log("Upgrade plan");
      const plan = this.pricingListSignal().find(planDetails => planDetails.name.toUpperCase() === this._preSelectedSubscriptionSignal()?.toUpperCase());
      this.submit(plan, this._preSelectedIntervalSignal(), this._currencySignal());
    }
  }

  private setPopular(popularPlan: SubscriptionType): void {
    if (Object.values(SUBSCRIPTION).includes(popularPlan)) {
      this._popularPlanSignal.set(popularPlan);
    }
  }

  private validateInputs(): void {
    !this._languageSignal() && this.setLanguage();
    !this._currencySignal() && this.getCurrentFromIp();
    !this._intervalSignal() && this.setInterval();
  }

  private checkForSalesMode(): void {
    const isPricePageDomain = (window.location.href).includes(this.deployUrl);
    let paramsObject;
    if (isPricePageDomain) {
      paramsObject = this.helperService.getParamsFromUrl(new URL(window.location.href));
    }
    // Check for web component view
    if (this._renderModeSignal() && !paramsObject) {
      // Check for sales web component view
      if (this._renderModeSignal() === this.sales && this.salesToken === SALES_TOKEN) {
        this._showCustomPlanSectionSignal.set(true);
      }
    } else {
      // Check for sales url support
      if (paramsObject?.mode === this.sales && paramsObject?.token === SALES_TOKEN) {
        // if mode and token are set, set mode to "sales" and shows a custom plan section
        this._renderModeSignal.set(RENDER_MODE.SALES);
        this._showCustomPlanSectionSignal.set(true);
        paramsObject?.language && this.setLanguage(paramsObject.language);
        paramsObject?.currency && this.setCurrency(paramsObject.currency);
        paramsObject?.interval && this.setInterval(paramsObject.interval);
        paramsObject?.popular && this.setPopular(paramsObject.popular);
        (paramsObject?.showMoreInterval === "true") && (this._showMoreIntervalSignal.set(true));
        this._showEnterpriseLitePlanSignal.set(paramsObject?.showEnterpriseLitePlan === 'true');
      } else {
        this._renderModeSignal.set(RENDER_MODE.WEBSITE);
      }
    }
    this.setPricingJson();
    !this._showEnterpriseLitePlanSignal() && this.removeEnterpriseLitePlan();
  }

  private removeEnterpriseLitePlan(): void {
    // if mode and token are not set, remove ENTERPRISE_LITE from pricing list
    const findEnterpriseLite = this.pricingListSignal().findIndex(plan => plan.name === SUBSCRIPTION.ENTERPRISELITE);
    if (findEnterpriseLite >= 0) {
      this.pricingListSignal.update(list => {
        const newList = [...list]; // Create a copy to avoid mutation
        newList.splice(findEnterpriseLite, 1);
        return newList;
      });
    }
  }

  protected toggleInterval(toggle: IntervalType | SalesIntervalType): void {
    this._intervalSignal.set(toggle);
    this.calculateCustomPlanPrice();
  }

  protected submit(priceModel: PricingDetails, interval: IntervalType | SalesIntervalType, currency: Currency): void {
    LoggerService.log("[Info] Selected plan", priceModel);
    LoggerService.log("[Info] Selected interval", interval);
    LoggerService.log("[Info] Selected currency", currency);
    let customPlan = {};
    if (priceModel?.min_workers < this.customPlanUserSignal()) {
      customPlan[this.customPlanUserType] = this.customPlanUserSignal() - priceModel.min_workers;
    }
    if (priceModel?.min_store < this.customPlanStoreSignal()) {
      customPlan[this.customPlanStoreType] = this.customPlanStoreSignal() - priceModel.min_store;
    }
    if (priceModel?.min_group_appointment < this.customPlanGroupAppointmentSignal()) {
      customPlan[this.customPlanGroupAppointmentType] = this.customPlanGroupAppointmentSignal() - priceModel.min_group_appointment;
    }
    if (priceModel?.min_conference < this.customPlanConferenceSignal()) {
      customPlan[this.customPlanConferenceType] = this.customPlanConferenceSignal() - priceModel.min_conference;
    }
    LoggerService.log("[Info] Selected customPlan", customPlan);
    const customEvent = new CustomEvent('pricingTablePlanSelectEvent', { detail: { priceModel, interval, currency, customPlan } });
    this.document.dispatchEvent(customEvent);
  }

  protected compareFeatureList(): void {
    const customEvent = new CustomEvent('pricingTableCompareFeatureEvent');
    this.document.dispatchEvent(customEvent);
  }

  private pricingTableProjectLoaded(): void {
    const customEvent = new CustomEvent('pricingTableLoadedEvent');
    this.document.dispatchEvent(customEvent);
  }

  protected toggleUserProfile(): void {
    this.isCustomPlanSelectionEnabledSignal.update(prev => !prev);
  }

  protected changeUserProfile(customNumber: number, flag: string): void {
    if (flag === this.customPlanUserType) {
      (customNumber < this.minLimit) && (this.customPlanUserSignal.set(this.minLimit));
    } else if (flag === this.customPlanGroupAppointmentType) {
      (customNumber < this.minLimit) && (this.customPlanGroupAppointmentSignal.set(this.minLimit));
    } else if (flag === this.customPlanStoreType) {
      (this.customPlanStoreSignal() < this.minLimit) && (this.customPlanStoreSignal.set(this.minLimit));
    } else if (flag === this.customPlanConferenceType) {
      if (customNumber === 0) {
        this.customPlanConferenceSignal.set(0);
      } else if (customNumber < 5) {
        this.customPlanConferenceSignal.set(5);
      }
    }
    this.calculateCustomPlanPrice();
  }

  private applyCustomProfileToPricingTable(): void {
    this.appliedPlanSignal.set(this.customSelectedPlanSignal());
  }

  protected cancel(): void {
    this.customPlanUserSignal.set(1);
    this.customPlanGroupAppointmentSignal.set(1);
    this.customPlanStoreSignal.set(1);
    this.customPlanConferenceSignal.set(0);
    this.appliedPlanSignal.set(undefined);
    this.calculateCustomPlanPrice();
    this.toggleUserProfile();
  }

  protected decrement(flag: string): void {
    if (flag === this.customPlanUserType) {
      (this.customPlanUserSignal() >= this.minLimit) && this.customPlanUserSignal.update(prev => prev - 1);
    } else if (flag === this.customPlanGroupAppointmentType) {
      (this.customPlanGroupAppointmentSignal() >= this.minLimit) && this.customPlanGroupAppointmentSignal.update(prev => prev - 1);
    } else if (flag === this.customPlanStoreType) {
      (this.customPlanStoreSignal() >= this.minLimit) && this.customPlanStoreSignal.update(prev => prev - 1);
    } else if (flag === this.customPlanConferenceType) {
      if (this.customPlanConferenceSignal() === 5) {
        this.customPlanConferenceSignal.set(0);
      } else if (this.customPlanConferenceSignal() >= this.minLimit) {
        this.customPlanConferenceSignal.update(prev => prev - 1);
      }
    }
    this.calculateCustomPlanPrice();
  }

  protected increment(flag: string): void {
    if (flag === this.customPlanUserType) {
      (this.customPlanUserSignal() < this.maxLimit) && this.customPlanUserSignal.update(prev => prev + 1);
    } else if (flag === this.customPlanGroupAppointmentType) {
      (this.customPlanGroupAppointmentSignal() < this.maxLimit) && this.customPlanGroupAppointmentSignal.update(prev => prev + 1);
    } else if (flag === this.customPlanStoreType) {
      (this.customPlanStoreSignal() < this.maxLimit) && this.customPlanStoreSignal.update(prev => prev + 1);
    } else if (flag === this.customPlanConferenceType) {
      if (this.customPlanConferenceSignal() === 0) {
        this.customPlanConferenceSignal.set(5);
      } else if (this.customPlanConferenceSignal() < this.maxLimit) {
        this.customPlanConferenceSignal.update(prev => prev + 1);
      }
    }
    this.calculateCustomPlanPrice();
  }

  private calculateCustomPlanPrice(): void {
    let preapredCustomPlan: PricingDetails;
    this.availablePlansSignal.set([]);
    this.pricingListSignal().forEach(plan => {
      if (
        plan.max_workers >= this.customPlanUserSignal() &&
        plan.max_store >= this.customPlanStoreSignal() &&
        plan.max_group_appointment >= this.customPlanGroupAppointmentSignal() &&
        plan.max_conference >= this.customPlanConferenceSignal()
      ) {
        this.availablePlansSignal.update(prev => [...prev, plan.id]);
      }
    });
    // here selecting the first available plan as default plan becuase we are showing it with precedence.
    preapredCustomPlan = this.pricingListSignal().find(plan => plan.id === this.availablePlansSignal()[0]);
    this.customSelectedPlanSignal.set(preapredCustomPlan);
    this.updateCustomFinalPlanTotal();
  }

  private updateCustomFinalPlanTotal(): void {
    // if custom selected plan is not set then return
    if (!this.customSelectedPlanSignal()) { return; }
    let customPlanTotal = 0;
    const additionPlanMonthlyYearlyMultiplier = (this._intervalSignal() === this.yearly) ? 1 : 1;
    customPlanTotal = +this.customSelectedPlanSignal()?.[this._currencySignal()?.toLowerCase() || this.eur.toLowerCase()]?.[this._intervalSignal()]?.original_price * additionPlanMonthlyYearlyMultiplier;
    if (this.customSelectedPlanSignal()?.min_workers < this.customPlanUserSignal()) {
      customPlanTotal = customPlanTotal + ((this.customPlanUserSignal() - this.customSelectedPlanSignal()?.min_workers) * this.extraUserCost * additionPlanMonthlyYearlyMultiplier);
    }
    if (this.customSelectedPlanSignal()?.min_store < this.customPlanStoreSignal()) {
      customPlanTotal = customPlanTotal + ((this.customPlanStoreSignal() - this.customSelectedPlanSignal()?.min_store) * this.extraStoreCost * additionPlanMonthlyYearlyMultiplier);
    }
    if (this.customSelectedPlanSignal()?.min_group_appointment < this.customPlanGroupAppointmentSignal()) {
      customPlanTotal = customPlanTotal + ((this.customPlanGroupAppointmentSignal() - this.customSelectedPlanSignal()?.min_group_appointment) * this.extraGroupAppointmentCost * additionPlanMonthlyYearlyMultiplier);
    }
    if (this.customSelectedPlanSignal()?.min_conference < this.customPlanConferenceSignal()) {
      customPlanTotal = customPlanTotal + ((this.customPlanConferenceSignal() - this.customSelectedPlanSignal()?.min_conference) * this.extraConferenceCost * additionPlanMonthlyYearlyMultiplier);
    }
    this.customPlanTotalAmountSignal.set(customPlanTotal);
    this.applyCustomProfileToPricingTable();
  }

  private getCurrentFromIp(): void {
    this.setCurrency(CURRENCY.CHF);
    this.helperService.getCurrentFromIp().subscribe({
      next: ipInfo => {
        const country = ipInfo?.country;
        let detectedCurrency: Currency = CURRENCY.EUR;
        if (country === 'CH' || country === 'LI') {
          detectedCurrency = CURRENCY.CHF;
        } else if (country === 'US') {
          detectedCurrency = CURRENCY.USD;
        } else if (country === 'GB' || country === 'UK') {
          detectedCurrency = CURRENCY.GBP;
        }
        this.setCurrency(detectedCurrency);
      },
      error: () => this.setCurrency(CURRENCY.CHF)
    })
  }

  private setPricingJson(): void {
    let tempPricingList: PricingDetails[];
    if (this._renderModeSignal() === this.dashboard) {
      tempPricingList = structuredClone(this.dashboardPricingList);
    } else if (this._renderModeSignal() === this.sales) {
      tempPricingList = structuredClone(this.salesPricingList);
    } else {
      tempPricingList = structuredClone(this.websitePricingList);
    }
    tempPricingList = this.filterPricingListBasedOnMinimumSubscription(tempPricingList);
    this.pricingListSignal.set(this.helperService.setupMaxLimiteInPricingList(tempPricingList, this._showUserBasedPricingSignal()));
  }

  private filterPricingListBasedOnMinimumSubscription(pricingList: PricingDetails[]): PricingDetails[] {
    if (this._minimumSubscriptionSignal()) {
      const minPrecedence = this.pricingPlanPrecedence?.[this._minimumSubscriptionSignal()];
      if (typeof minPrecedence === 'number') {
        pricingList = pricingList.filter(plan => this.pricingPlanPrecedence?.[plan.id] >= minPrecedence);
      }
    }
    return pricingList;
  }
}

